/**
 * Drone D1 #345 — the aggregated "drones on this incident" view. drone_missions.incident_id is
 * NON-UNIQUE, so an ops centre can stack several missions (across operators + pricing tiers) on one
 * incident for continuous coverage. This panel lists them all — operator, status, the committed
 * on-station window, and whether a live feed is up — and, when an area of interest is supplied,
 * offers a "Request another drone" affordance (the inline RequestLiveEyes flow targeting the same
 * incident). Read surface + one request affordance; staggered-window scheduling is a later increment.
 */
import { useEffect, useState, type JSX } from 'react';
import { isMissionFeedLive } from '@tsr/shared-types';
import { fetchDroneMissions, type DroneMissionView } from './api';
import { missionOnStationMinutes, missionStatusBadge } from './droneMissionUi';
import { lifecycleStateStyle } from './styles/lifecycleStates';
import { RequestLiveEyes } from './RequestLiveEyes';

const INCIDENT_DRONES_POLL_MS = 30_000;

function MissionRow({
  m,
  isCurrent,
  onSelectMission,
}: {
  m: DroneMissionView;
  isCurrent: boolean;
  /** Phase 4e — open this mission's {kind:'mission'} detail (where the live feed plays). When
   *  omitted, or for the mission already open (isCurrent), the live-feed indicator stays static. */
  onSelectMission?: (m: DroneMissionView) => void;
}): JSX.Element {
  const badge = missionStatusBadge(m.status);
  const onStation = missionOnStationMinutes(m);
  const live = isMissionFeedLive(m.status);
  // Phase 4d — the IMS resource-lifecycle band, BESIDE the marketplace status badge, so a tasked
  // drone reads in the same lifecycle language as every other resource. Null = no resource line.
  const band = m.lifecycle_band ? lifecycleStateStyle(m.lifecycle_band) : null;
  return (
    <li
      className={`rounded border px-2.5 py-1.5 ${
        isCurrent ? 'border-cyan-500/50 bg-cyan-500/5' : 'border-slate-700/60 bg-slate-800/40'
      }`}
      data-testid="incident-drone-row"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[12px] font-medium truncate">
            {m.operator_name ?? 'Operator TBD'}
            {isCurrent && <span className="ml-1 text-[10px] text-cyan-300">(this mission)</span>}
          </p>
          <p className="text-[10px] text-slate-400 truncate">{m.asset_name ?? 'Asset TBD'}</p>
        </div>
        <div className="shrink-0 flex items-center gap-1">
          {band && (
            <span
              data-testid="mission-lifecycle-band"
              className="text-[10px] px-1.5 py-0.5 rounded font-medium"
              style={{ backgroundColor: `${band.color}33`, color: band.color }}
              title={`IMS resource lifecycle: ${band.label}`}
            >
              {band.label}
            </span>
          )}
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${badge.cls}`}>
            {badge.label}
          </span>
        </div>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-slate-400">
        <span>{onStation != null ? `${onStation} min eyes-on` : 'eyes-on TBD'}</span>
        {m.quoted_price_cad != null && (
          <span>{m.quoted_price_cad === 0 ? 'No charge' : `$${m.quoted_price_cad.toLocaleString()} CAD`}</span>
        )}
        {live &&
          (onSelectMission && !isCurrent ? (
            <button
              type="button"
              onClick={() => onSelectMission(m)}
              className="text-cyan-300 hover:text-cyan-200 hover:underline"
              data-testid="incident-drone-live"
              title="Open this mission's live feed"
            >
              ● live feed
            </button>
          ) : (
            <span className="text-cyan-300" data-testid="incident-drone-live">
              ● live feed
            </span>
          ))}
      </div>
    </li>
  );
}

export function IncidentDronesPanel({
  incidentId,
  currentMissionId = null,
  requestAo = null,
  aoLabel = 'this incident',
  onSelectMission,
}: {
  incidentId: string;
  /** Highlight + label the mission currently open (when rendered inside a mission detail). */
  currentMissionId?: string | null;
  /** When provided, show a "Request another drone" affordance (inline RequestLiveEyes). Omit on
   *  the incident panel, where the standalone Request-live-eyes affordance already exists. */
  requestAo?: { lat: number; lon: number } | null;
  aoLabel?: string;
  /** Phase 4e — open a drone's {kind:'mission'} detail (where the live feed plays) from its row,
   *  closing the Situation-Unit live-feed dead-end (§I.2). Omit ⇒ rows stay read-only. */
  onSelectMission?: (m: DroneMissionView) => void;
}): JSX.Element {
  const [missions, setMissions] = useState<DroneMissionView[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showRequest, setShowRequest] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    let alive = true;
    void fetchDroneMissions(ac.signal, { incidentId })
      .then((ms) => {
        if (alive) setMissions(ms);
      })
      .catch(() => {});
    return () => {
      alive = false;
      ac.abort();
    };
  }, [incidentId, refreshKey]);

  // Light poll so statuses + a new feed appear without a manual refresh (matches the rail idiom).
  useEffect(() => {
    const t = setInterval(() => setRefreshKey((k) => k + 1), INCIDENT_DRONES_POLL_MS);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="border-t border-slate-800 pt-3" data-testid="incident-drones-panel">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <h3 className="text-xs uppercase tracking-wider text-slate-400">
          Drones on this incident
          <span className="ml-1.5 text-slate-500" data-testid="incident-drones-count">
            ({missions.length})
          </span>
        </h3>
        {requestAo && (
          <button
            type="button"
            data-testid="request-another-drone"
            onClick={() => setShowRequest((v) => !v)}
            className="text-[11px] rounded border border-slate-600/60 px-2 py-0.5 text-slate-300 hover:border-slate-400 hover:text-slate-100"
          >
            {showRequest ? 'Close' : 'Request another drone'}
          </button>
        )}
      </div>

      {missions.length === 0 ? (
        <p className="text-[11px] text-slate-500">No drone missions on this incident yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {missions.map((m) => (
            <MissionRow
              key={m.id}
              m={m}
              isCurrent={m.id === currentMissionId}
              onSelectMission={onSelectMission}
            />
          ))}
        </ul>
      )}

      {requestAo && showRequest && (
        <div className="mt-2">
          <RequestLiveEyes
            ao={requestAo}
            aoLabel={aoLabel}
            incidentId={incidentId}
            onMissionCreated={() => {
              setRefreshKey((k) => k + 1);
              setShowRequest(false);
            }}
          />
        </div>
      )}
    </section>
  );
}
